// Copyright 2019 Sorama B.V.

import { ObjectExtensions } from "extensions/ObjectExtensions";

export class CustomizableAttributeTypes {
    public static readonly color: string = "color";
    public static readonly number: string = "number";

    public readonly color: string = CustomizableAttributeTypes.color;
    public readonly number: string = CustomizableAttributeTypes.number;

    public static parse(customAttrib: string): { name: string; type: string } {
        const splits = customAttrib.split(":");
        if (splits.length !== 2) return undefined;

        const t = splits[1].toLowerCase();
        if (!CustomizableAttributeTypes.valid(t)) return undefined;

        return { name: splits[0], type: t };
    }

    public static valid(t: string): boolean {
        return t === CustomizableAttributeTypes.color || t === CustomizableAttributeTypes.number;
    }
}

export class SVGNode {
    private _part: string;
    private _tag: string;
    private _value: string;
    private _attributes: { [attribute: string]: string } = {};
    private _children: SVGNode[] = [];
    private _customizableAttributes: { [name: string]: { attribute: string[]; type: string } } = {};

    public get tag(): string {
        return this._tag;
    }
    public get children(): SVGNode[] {
        return this._children;
    }

    public get part(): string {
        return this._part;
    }

    public get customizableAttributes(): { [name: string]: { attribute: string[]; type: string } } {
        return this._customizableAttributes;
    }

    public get attributes(): { [name: string]: string } {
        return this._attributes;
    }

    public get bounds(): { x: number; y: number; width: number; height: number } {
        if (this.tag !== "svg") return undefined;

        const attrib = this.attributes["viewBox"];

        if (!attrib) return undefined;

        const splits = attrib.split(",");
        if (splits.length != 4) return undefined;

        return {
            x: Number(splits[0]),
            y: Number(splits[1]),
            width: Number(splits[2]),
            height: Number(splits[3]),
        };
    }

    constructor(elementOrTagName: Element | string, value?: string, part?: string) {
        if (typeof elementOrTagName === "string") {
            this._tag = <string>elementOrTagName;
            this._value = value;
            this._part = part;
            return;
        } else {
            const element = <Element>elementOrTagName;

            this._tag = element.tagName;
            this._value = element.textContent;

            // Get all attributes
            for (let i = 0; i < element.attributes.length; i++) {
                const attrib = element.attributes[i];
                const name = attrib.name.toLowerCase();
                const value = attrib.value;

                if (name === "svg-part") {
                    this._part = value;
                    continue;
                }

                if (name.endsWith(".bind") || name === "class") continue; // Ignore binding attributes and class

                if (name.endsWith(".c")) {
                    const customAttrib = CustomizableAttributeTypes.parse(value);
                    if (!customAttrib) continue;

                    // Custom attribute
                    const splits = name.split(".");
                    if (!splits[0] || splits[0] === "") continue; // Invalid attribute

                    const attribute = splits[0];
                    const existingAttrib = this._customizableAttributes[customAttrib.name];
                    if (existingAttrib) existingAttrib.attribute.push(attribute);
                    else
                        this._customizableAttributes[customAttrib.name] = {
                            attribute: [attribute],
                            type: customAttrib.type,
                        };
                } else {
                    this._attributes[attrib.name] = value;
                }
            }

            for (let i = 0; i < element.children.length; i++) {
                this._children.push(new SVGNode(element.children[i]));
            }
        }

        this._part = part;
    }

    public transform(newTag: string, stripAtributes = true): SVGNode {
        this._tag = newTag;
        if (stripAtributes) {
            this._attributes = {};
        }

        return this;
    }

    public setAttribute(parameter: string, value: string, recursive = false) {
        if (value === null || value === undefined) delete this._attributes[parameter];
        else this._attributes[parameter] = value;

        if (recursive) {
            for (const child of this.children) {
                child.setAttribute(parameter, value, recursive);
            }
        }
    }

    public addCustomizableAttribute(name: string, attribute: string, type: string) {
        if (!CustomizableAttributeTypes.valid(type)) throw new Error("Invalid customizable attribute type");

        const existing = this._customizableAttributes[name];
        if (existing) {
            if (existing.type !== type) throw new Error("Custom attribute already exists with different type");

            existing.attribute.push(attribute);
        } else {
            this._customizableAttributes[name] = { attribute: [attribute], type: type };
        }
    }

    public getAllCustomizableAttributes(): { [name: string]: { attribute: string[]; type: string } } {
        const attribs = <{ [name: string]: { attribute: string[]; type: string } }>{};

        for (const key in this.customizableAttributes) {
            if (!Object.prototype.hasOwnProperty.call(this.customizableAttributes, key)) continue;

            attribs[key] = this.customizableAttributes[key];
        }

        for (const child of this.children) {
            const attrs = child.getAllCustomizableAttributes();
            for (const key in attrs) {
                if (!Object.prototype.hasOwnProperty.call(attrs, key)) continue;

                attribs[key] = attrs[key];
            }
        }

        return attribs;
    }

    public getParts(): string[] {
        let parts: string[] = [];

        for (const child of this._children) {
            const childParts = child.getParts();
            parts = parts.concat(childParts.filter((x) => parts.every((y) => y !== x)));
        }

        if (this._part && !parts.some((x) => x === this._part)) {
            parts.push(this._part);
        }

        return parts;
    }

    public removeAttribute(attribute: string) {
        delete this._attributes[attribute];
    }

    public addChild(node: SVGNode, index?: number): void {
        if (index !== null && index !== undefined) {
            this.children.splice(index, 0, node);
        } else {
            this.children.push(node);
        }
    }

    public removeChild(index: number): void {
        this._children.splice(index, 1);
    }

    public find(pred: (node: SVGNode) => boolean): SVGNode {
        if (pred(this)) return this;

        for (const child of this.children) {
            const result = child.find(pred);
            if (result != undefined) return result;
        }

        return undefined;
    }

    public findAll(pred: (node: SVGNode) => boolean): SVGNode[] {
        let matches: SVGNode[] = [];

        if (pred(this)) matches.push(this);

        for (const child of this.children) {
            const result = child.findAll(pred);

            matches = matches.concat(...result);
        }

        return matches;
    }

    public updateWhere(pred: (node: SVGNode) => boolean, update: (node: SVGNode) => unknown) {
        this.findAll(pred).forEach(update);
    }

    public clearChildren(): void {
        this._children = [];
    }

    public clone(): SVGNode {
        // Create a new node
        const node = new SVGNode(this._tag, this._value, this._part);

        // Copy all attributes
        ObjectExtensions.forEachPropertyInSet(this._attributes, (key, value) => {
            node.setAttribute(key, this._attributes[key]);
        });

        // Copy custom attributes
        ObjectExtensions.forEachPropertyInSet(this._customizableAttributes, (key, value) => {
            for (const attrib of value.attribute) {
                node.addCustomizableAttribute(key, attrib, value.type);
            }
        });

        // Copy children
        for (const child of this._children) {
            node.addChild(child.clone());
        }

        return node;
    }

    public render(customizableAttributes: { [name: string]: string }, indentLevel?: number): string {
        if (!indentLevel) indentLevel = 0;

        let svg = "";
        for (let i = 0; i < indentLevel; i++) {
            svg += "    ";
        }

        svg += "<" + this._tag;
        const addedAttributes = <string[]>[];

        for (const key in customizableAttributes) {
            if (!Object.prototype.hasOwnProperty.call(customizableAttributes, key)) continue;

            if (!Object.prototype.hasOwnProperty.call(this._customizableAttributes, key)) continue;

            const s = this._customizableAttributes[key];
            let attribute = s.attribute;

            attribute = attribute.filter((x) => !addedAttributes.some((y) => y === x));

            if (attribute.length === 0) continue;

            for (const attrib of attribute) {
                svg += " " + attrib + "='" + customizableAttributes[key] + "'";
                addedAttributes.push(attrib);
            }
        }

        for (const key in this._attributes) {
            if (!Object.prototype.hasOwnProperty.call(this._attributes, key)) continue;

            if (addedAttributes.some((x) => x === key)) continue;

            svg += ` ${key}="${this._attributes[key]}"`;
            addedAttributes.push(key);
        }

        svg += ">\n";

        // Render children
        for (const child of this._children) {
            svg += child.render(customizableAttributes, indentLevel + 1);
        }

        if (this._value) {
            for (let i = 0; i < indentLevel; i++) {
                svg += "    ";
            }
            svg += this._value + "\n";
        }

        for (let i = 0; i < indentLevel; i++) {
            svg += "    ";
        }

        svg += "</" + this._tag + ">\n";

        return svg;
    }

    public static getSvgsInElement(element: Element): SVGNode[] {
        const nodes = <SVGNode[]>[];

        if (element.tagName.toLowerCase() === "svg") {
            return [new SVGNode(element)];
        }

        for (let i = 0; i < element.children.length; i++) {
            const child = element.children[i];
            if (child.tagName.toLowerCase() === "svg") {
                nodes.push(new SVGNode(child));
            } else {
                const subNodes = this.getSvgsInElement(child);
                for (const node of subNodes) {
                    nodes.push(node);
                }
            }
        }

        return nodes;
    }
}
